import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import {
  ProfilePreferences,
  ProfilePreferencesField,
} from '../models/preferences';
import {
  editPreferences,
  getPreferences,
} from '../services/api/profileServices';
import {
  getEmployeeProjects,
  getEmployeeTasks,
} from '../services/api/employeeServices';
import { selectProfileId } from './profileSlice';
import { generateProjectLabel } from '../services/projectService';

export const getPreferencesThunk = createAsyncThunk<
  ProfilePreferences,
  void,
  { state: RootState }
>('preferences', async (_, { getState }) => {
  const profileId = selectProfileId(getState());

  const userPreferences: ProfilePreferences = {
    [ProfilePreferencesField.VIEW_MODE]: 0,
    [ProfilePreferencesField.PROJECT]: null,
    [ProfilePreferencesField.TASK]: null,
    [ProfilePreferencesField.EXPORT_SHOW_CONTRACT_TYPE]: false,
    [ProfilePreferencesField.EXPORT_SHOW_PROJECT_MANAGER]: false,
  };

  const preferencesResponse = await getPreferences();
  userPreferences[ProfilePreferencesField.VIEW_MODE] =
    preferencesResponse.activityView ?? 0;
  userPreferences[ProfilePreferencesField.EXPORT_SHOW_CONTRACT_TYPE] =
    preferencesResponse.exportDetailsShowContractType ?? false;
  userPreferences[ProfilePreferencesField.EXPORT_SHOW_PROJECT_MANAGER] =
    preferencesResponse.exportDetailsShowProjectManager ?? false;

  const { defaultProjectId, defaultTaskId } = preferencesResponse;

  if (!defaultProjectId) return userPreferences;

  const projects = await getEmployeeProjects({
    internalId: profileId || '',
  });

  const preferredProject = projects.items.find(
    (project) => project.id === defaultProjectId
  );

  if (preferredProject) {
    userPreferences[ProfilePreferencesField.PROJECT] = {
      id: preferredProject.id,
      label: generateProjectLabel(preferredProject.name, preferredProject.code),
      isAMS: preferredProject.isAMS,
    };
  }

  const tasks = await getEmployeeTasks({
    internalId: profileId || '',
    projectId: defaultProjectId,
  });

  const preferredTask = tasks.items.find((task) => task.id === defaultTaskId);

  if (preferredTask) {
    userPreferences[ProfilePreferencesField.TASK] = preferredTask;
  }

  return userPreferences;
});

export const savePreferenceThunk = createAsyncThunk<
  ProfilePreferences,
  ProfilePreferences
>('savePreferences', async (arg: ProfilePreferences) => {
  await editPreferences(arg);

  return arg;
});

export interface PreferecesState {
  preference: ProfilePreferences;
  isLoading: boolean;
  isSaveLoading: boolean;
  saveError: unknown | null;
  error: unknown | null;
}

const initialState: PreferecesState = {
  preference: {
    [ProfilePreferencesField.VIEW_MODE]: 0,
    [ProfilePreferencesField.PROJECT]: null,
    [ProfilePreferencesField.TASK]: null,
    [ProfilePreferencesField.EXPORT_SHOW_CONTRACT_TYPE]: false,
    [ProfilePreferencesField.EXPORT_SHOW_PROJECT_MANAGER]: false,
  },
  isLoading: true,
  isSaveLoading: false,
  saveError: null,
  error: null,
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    changePreferences: (state, action) => {
      state.preference = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPreferencesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(getPreferencesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = false;
        state.preference = action.payload;
      })
      .addCase(getPreferencesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      })
      .addCase(savePreferenceThunk.pending, (state) => {
        state.isSaveLoading = true;
      })
      .addCase(savePreferenceThunk.fulfilled, (state, action) => {
        state.isSaveLoading = false;
        state.preference = action.payload;
      })
      .addCase(savePreferenceThunk.rejected, (state, action) => {
        state.isSaveLoading = false;
        state.saveError = action.error;
      });
  },
});

export const preferencesReducer = preferencesSlice.reducer;
export const { changePreferences } = preferencesSlice.actions;

// selectors
export const selectPreferences = (root: RootState): ProfilePreferences =>
  root.preferences.preference;

export const selectLoadingPreferences = (root: RootState): boolean =>
  root.preferences.isLoading;
export const selectErrorPreferences = (root: RootState): unknown | null =>
  root.preferences.error;
